/* eslint-disable @typescript-eslint/dot-notation */
import { Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ContainerType, ContainerVm, ResourceVm, SponsorVm } from 'qp-ui-interface';
import { NewrelicService } from 'src/app/analytics/newrelic/service/newrelic.service';
import { DataService } from '../../../data.service';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { AdobeService } from '../../services/adobe/adobe.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { NavigationLibraryService } from '../../services/navigation/navigation.service';
import { RoutingService } from '../../services/routing/routing.service';
import { ScreenService } from '../../services/screen/screen.service';
import { TabService } from '../../services/tabs/tab.service';
import { validateToPlay } from '../../utils/playerUtils';
import { CarouselStyle, CarouselType } from './model/cards-carousel-models';

@Component({
  selector: 'app-cards-carousel',
  templateUrl: './cards-carousel.component.html',
  styleUrls: ['./cards-carousel.component.scss'],
})
export class CardsCarouselComponent implements AfterViewInit, OnInit {
  @ViewChild('carouselContainer') carouselContainer: ElementRef<HTMLElement>;
  //#region Inputs & Outputs
  @Output() getLoadingStatusSubmit = new EventEmitter<boolean>(true);
  @Output() clickAction = new EventEmitter<ResourceVm>();
  @Output() cardWithFocus = new EventEmitter<number>();
  @Input() container!: ContainerVm;
  @Input() verticalIndex = 0;
  @Input() carouselType: CarouselType = CarouselType.SQUARE_REGULAR;
  @Input() customClickAction = false;
  @Input() navigateToPlayer = false;
  @Input() showPlayIcon = false;
  @Input() showProgress = false;
  @Input() isPopup = false;
  @Input() showName = true;
  @Input() showTag = true;
  @Input() className = '';
  @Input() containerToCenter: string;
  @Input() sponsor: SponsorVm;
  @Input() isPlayerCarousel = false;
  @Input() source: string; //page url where the carousel was placed
  @Input() dataUp: string = null;
  //#endregion

  //#region Component vars
  hasFocusedFirstCard = false;
  move = {
    right: '',
    bottom: '',
  };
  showLeftArrow = false;
  showRightArrow = false;
  private actualMovement = 0;
  private alreadyClicked = false;
  entitlementPackages: string[];
  //#endregion

  constructor(
    private router: Router,
    private tabService: TabService,
    private dataService: DataService,
    private screenService: ScreenService,
    private location: Location,
    private navService: NavigationLibraryService,
    private readonly routingService: RoutingService,
    private readonly localStorage: LocalStorageService,
    private readonly el: ElementRef,
    private readonly adobeService: AdobeService,
    private readonly newrelic: NewrelicService
  ) {}

  //#region Angular component functions
  ngOnInit(): void {
    this.entitlementPackages = this.adobeService.fullEntitlementPackages?.reduce((finalObj: string[], currEnt: string) => {
      const foundTvIndex = currEnt.indexOf('tve:');
      const entitlementToAdd = currEnt.slice(foundTvIndex).replace('tve:', '');
      finalObj.push(entitlementToAdd);
      return finalObj;
    }, []);
  }

  ngAfterViewInit(): void {
    this.getLoadingStatusSubmit.emit(false);
  }
  //#endregion

  //#region Getters
  get carouselTypes(): typeof CarouselType {
    return CarouselType;
  }

  get carouselStyles(): typeof CarouselStyle {
    return CarouselStyle;
  }

  get containerName(): string {
    return this.container?.name;
  }

  get containerId(): string {
    return this.container?.id;
  }
  //#endregion

  getImageWidth(item: ResourceVm): string {
    return item.image;
  }

  /**
   * Focus container
   *
   * @param event
   */
  focusOnContainer(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    // Check if the new focus target is not inside the container
    if (relatedTarget && !this.carouselContainer.nativeElement.contains(relatedTarget)) {
      this.hasFocusedFirstCard = false;
    }
  }

  /**
   * Called by html to start redirection
   *
   * @param card element to redirect
   * @param card undefined means view more card click
   * @returns
   */
  async watchMovie(card: ResourceVm): Promise<void> {
    // if already click dont do anything
    if (this.alreadyClicked) {
      return;
    }
    this.alreadyClicked = true;
    if (!card) {
      // view more card click
      this.goToViewMore();
      this.alreadyClicked = false;
      return;
    } else if (card.type === 'collection') {
      this.goToCollection(card);
      this.alreadyClicked = false;
      return;
    }

    this.screenService.searchItemPosition = (this.navService.getHorizontalIndex() + 1).toString();
    this.dataService.playbackStartTime = new Date().getTime();

    // If user wants to personalize the click action just return the card and let the user handle it
    if (this.customClickAction) {
      this.clickAction.emit(card);
      return;
    }
    this.storeDetailsPageData(card);
    this.dataService.collection = [];
    if (!this.location.path().includes('movie') && !this.location.path().includes('series') && !this.location.path().includes('episode')) {
      this.screenService.searchlink = this.location.path();
    } else if (
      (this.location.path().includes('movie') || this.location.path().includes('series') || this.location.path().includes('episode')) &&
      this.screenService.searchlink === '/search'
    ) {
      this.screenService.searchlink = this.location.path();
    }
    const ableToRedirect = await this.redirect(card);

    if (!ableToRedirect) {
      this.alreadyClicked = false;
    }
    this.alreadyClicked = false;
    this.localStorage.removeItem(LocalStorageKeys.TAB_CHANGED);
  }

  resetInitialPosition(): void {
    this.move.right = '';
    this.move.bottom = '';
    this.actualMovement = 0;
  }

  keyDownImage(e, length: number, isVertical?: boolean): void {
    const keyCodes = {
      subtract: isVertical ? 40 : 39,
      add: isVertical ? 38 : 37,
      ignoredKeys: [isVertical ? 39 : 40, isVertical ? 37 : 38],
    };
    const sizes = {
      axis: isVertical ? 'height' : 'width',
      margins: isVertical ? ['top', 'bottom'] : ['right', 'left'],
      move: isVertical ? 'bottom' : 'right',
    };
    this.navService.setIndexContainer('container-all', this.verticalIndex);
    // Getting card design to move carousel
    const traget = e.target as HTMLElement;

    const imageParent = traget.className.includes('image-parent')
      ? traget
      : (traget.getElementsByClassName('image-parent')[0] as HTMLElement);

    const imageParentWidth = this.getStyleValue(imageParent, sizes.axis);
    const imageParentMargin =
      this.getStyleValue(imageParent, `margin-${sizes.margins[0]}`) + this.getStyleValue(imageParent, `margin-${sizes.margins[1]}`);
    // Resting 1 -> the last card that need to stay on screen
    if (e.keyCode === keyCodes.subtract && (this.actualMovement < length - 1 || (this.container.viewAll && this.actualMovement < length))) {
      this.actualMovement += 1;
      this.move[sizes.move] = `${(imageParentWidth + imageParentMargin) * this.actualMovement}px`;
      if (this.actualMovement >= length - 1) {
        this.showRightArrow = false;
      }
    } else if (e.keyCode === keyCodes.add && this.actualMovement > 0) {
      this.actualMovement -= 1;
      this.move[sizes.move] = `${(imageParentWidth + imageParentMargin) * this.actualMovement}px`;
      if (this.actualMovement === 0) {
        this.showLeftArrow = false;
      }
    } else if (keyCodes.ignoredKeys.includes(e.keyCode)) {
      this.showRightArrow = false;
      this.showLeftArrow = false;
    } else if (e.keyCode === keyCodes.add && this.actualMovement === 0) {
      this.actualMovement = 0;
    }
  }

  private goToCollection(card): void {
    this.localStorage.removeItem(LocalStorageKeys.TAB_CHANGED);
    this.screenService.searchItemPosition = (this.navService.getHorizontalIndex() + 1).toString();
    card.containerId = this.containerId;
    card.containerName = this.containerName;
    card.containerType = this.container.type as ContainerType;
    const collectionType = card.collectionLayout === 'storefront' ? 'collection' : 'genres'; // check if is artist
    if (`/${collectionType}/${card.id}` !== this.location.path()) {
      this.screenService.searchlink = this.location.path();
    }
    this.router.navigateByUrl(`/${collectionType}/${card.id}`, {
      state: { resource: card, container: this.container },
    });
  }

  private goToViewMore() {
    if (!this.container) throw 'Container undefined';
    this.localStorage.removeItem(LocalStorageKeys.TAB_CHANGED);
    this.screenService.searchlink = this.location.path();
    this.screenService.searchItemPosition = (this.navService.getHorizontalIndex() + 1).toString();
    const id = this.container.id;
    const viewMoreUrl = this.container.viewMoreUrl;
    this.router.navigateByUrl(`/view-all/${id}`, {
      state: { container: this.container, viewMoreUrl },
    });
  }

  onEnterCard(event: MouseEvent, index: number): void {
    this.navService.focusElement(event.target as HTMLElement, index, this.verticalIndex);
  }

  onFocus(event: FocusEvent, index: number): void {
    this.navService.setIndexContainer('container-all', this.verticalIndex);
    if (index > 0) {
      this.hasFocusedFirstCard = true;
    }
    this.navService.addAutoscroll(event.target as Element);
    if (!!this.containerToCenter) {
      const routerContainer = document.getElementsByClassName(this.containerToCenter);
      // handle center since default lib center function work on the the container-all
      // container-all includes the hero carousel, so it will not work
      if (routerContainer.length > 0) {
        const elementTop = this.el.nativeElement.offsetTop;
        const divTop = routerContainer[0] as HTMLElement;
        const elementRelativeTop = elementTop - divTop.offsetTop;
        divTop.scrollTop = elementRelativeTop;
      }
    }
    this.cardWithFocus.emit(index);
  }

  onFocusOut(event: FocusEvent): void {
    this.navService.removeAutoscroll(event.target as Element);
  }

  private async redirect(card: ResourceVm): Promise<boolean> {
    card.containerId = this.containerId;
    card.containerName = this.containerName;
    card.containerType = this.container.type as ContainerType;
    this.newrelic.store.setContainer(this.container);
    this.newrelic.store.setResource(card);
    if (
      this.isPlayerCarousel ||
      validateToPlay(card.type) ||
      this.showProgress ||
      (card.type.includes('episode') &&
        this.carouselType === CarouselType.SQUARE_REGULAR_WITH_DESCRIPTION &&
        (this.dataService.currentUrl.includes('episode') || this.dataService.currentUrl.includes('series')))
    ) {
      const navigated = await this.routingService.navigateToPlayerWithValidations(card, this.showProgress, this.source, true);
      if (this.navigateToPlayer && navigated) {
        this.clickAction.emit(card);
      }
      return navigated;
    } else if (card.contentType.includes('airing')) {
      // navigate to player with the channel data
      card.contentType = 'channel';
      card.type = 'channel';
      card.id = card.channelId;
      const navigated = await this.routingService.navigateToPlayerWithValidations(card, this.showProgress, this.source, true);
      if (this.navigateToPlayer && navigated) {
        this.clickAction.emit(card);
      }
      return navigated;
    } else if (['trailer', 'tvbonus'].includes(card.type) || this.navigateToPlayer) {
      if (this.navigateToPlayer) {
        this.clickAction.emit(card);
      }

      this.dataService.playbackStartTime = new Date().getTime();
      return this.routingService.navigateDirectoToPlayer(card);
    } else {
      return this.routingService.navigateToDetails(card, this.container);
    }
  }

  /**
   * Function to store all the information needed in the details page for NR
   *
   * @param card resourcevm
   */
  private storeDetailsPageData(card: ResourceVm): void {
    // Store the currentTabId
    if (card.tabId && !this.tabService.currentTabId) {
      this.tabService.currentTabId = card.tabId;
    } else if (!this.tabService.currentTabId) {
      this.tabService.currentTabId = '0';
    }
    // Store the currenTabName
    if (card.tabName && card.tabName === 'All' && !this.tabService.currentTabName) {
      this.tabService.currentTabName = 'Home';
    } else if (card.tabName && !this.tabService.currentTabName) {
      this.tabService.currentTabName = card.tabName;
    } else if (!this.tabService.currentTabName) {
      this.tabService.currentTabName = ' ';
    }
  }

  /**
   * Function to get the value of a style property from an element
   *
   * @param element HTMLElement
   * @param style property name from style needed
   * @returns number value of style property
   */
  private getStyleValue(element: HTMLElement, style: string): number {
    return +getComputedStyle(element).getPropertyValue(style).split('px')[0];
  }

  /**
   * @param item ResourceVm to verify
   * @returns a boolean to determine wether or not to show locked icon
   **/
  showBlocked = (item: ResourceVm): boolean => {
    let hasPermission = false;
    const dtcs = this.extractDTCs(item);
    if (dtcs && dtcs.length) {
      hasPermission = dtcs[0] === 'free' ? true : dtcs.every((dtc) => this.entitlementPackages.includes(dtc));
    }
    return item.isPremium ? !hasPermission : false;
  };

  /**
   * @param container ResourceVm to extract dtcs
   * @returns a string array with all the dtcs of the content
   **/
  extractDTCs = (container: ResourceVm): string[] => {
    const dtcs: string[] = container.entitlement?.reduce((acc: string[], currEnt) => {
      // Verifies each subscriptionPackage for each entitlement
      (currEnt.subscriptionPackage || []).forEach((pkg: string) => {
        // Checks for a dtc match to add it
        const match = pkg.match(/dtc:([a-zA-Z0-9_-]+)/);
        if (match) acc.push(match[1]); // Add only the matched DTC value
      });
      return acc;
    }, []);
    return dtcs;
  };
}
